/**
 * #1320 — Settings Recent rail.
 *
 * Per-user, server-side preference: persists across sessions AND devices
 * (the ticket explicitly rules out localStorage). The list lives on the
 * `User.permissions._settingsRecents` JSON blob — same pattern the
 * existing table-state hook (WAYP-175) uses for column visibility.
 *
 * The blob shape (mirrored on the server) reserves `pinned[]` slots for
 * the upcoming "pin a setting" feature so the data layout doesn't have
 * to migrate when that lands. Today only `recent[]` is written to.
 */
import { useCallback, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../lib/api';

export interface SettingsRecentsState {
  pinned: string[];
  recent: string[];
}

export const SETTINGS_RECENTS_QUERY_KEY = ['settings-recents'] as const;

/**
 * How many slots the Recent rail shows in the Settings palette. Pinned
 * items take their slots first (#1321 follow-up — pinning ships in a
 * future ticket), recents fill the remainder.
 */
export const RECENT_PANEL_SLOTS = 3;
export const PINNED_SLOT_COUNT = 0; // bumps to e.g. 2 when pinning ships

export function useSettingsRecents() {
  return useQuery({
    queryKey: SETTINGS_RECENTS_QUERY_KEY,
    queryFn: async () => {
      const res = await api.get<{ data: SettingsRecentsState }>(
        '/users/me/settings-recents',
      );
      return res.data.data.recent;
    },
    // The rail rarely changes more often than the user navigates Settings.
    // A 5-minute stale window dodges thrash when the user pops in and out
    // of the palette without slowing down the genuine "I just visited X"
    // update path (which writes through `useTrackSettingsRecent` and
    // invalidates this query directly).
    staleTime: 5 * 60 * 1000,
    // The rail is a soft, decorative feature — if the prefs endpoint is
    // briefly offline we just show an empty rail; no need to retry.
    retry: false,
  });
}

/**
 * Hook returning a stable callback that records a Settings page visit.
 * Deduped at the call site against the most-recently-tracked href to
 * keep network traffic minimal when the user navigates inside a single
 * page (e.g. tab changes that don't actually swap the route).
 */
export function useTrackSettingsRecent() {
  const queryClient = useQueryClient();
  const lastTrackedRef = useRef<string | null>(null);

  const mutation = useMutation({
    mutationFn: async (href: string) => {
      const res = await api.post<{ data: SettingsRecentsState }>(
        '/users/me/settings-recents',
        { href },
      );
      return res.data.data;
    },
    onSuccess: (data) => {
      // Push the fresh `recent[]` straight into the cache so the rail
      // updates without a refetch.
      queryClient.setQueryData(SETTINGS_RECENTS_QUERY_KEY, data.recent);
    },
    onError: () => {
      // Best-effort tracking — silently swallow. The rail will catch up
      // on the next successful track call. Same trade-off as the
      // table-state persistence path (see use-table-state.ts).
    },
    retry: false,
  });

  return useCallback(
    (href: string) => {
      if (!href || lastTrackedRef.current === href) return;
      lastTrackedRef.current = href;
      mutation.mutate(href);
    },
    [mutation],
  );
}
